import React, { useState, useEffect, useRef, useCallback } from 'react';

const FlappyBird = () => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('menu'); // menu, playing, gameOver
  const [gameMode, setGameMode] = useState(null); // 'easy' or 'hard'
  const [score, setScore] = useState(0);
  const [highScores, setHighScores] = useState({ easy: 0, hard: 0 });
  
  const gameModes = useRef({
    easy: {
      gravity: 0.25,
      jump: -6,
      pipeGap: 220,
      pipeSpeed: 2.5,
      pipeInterval: 120,
      label: 'Easy',
      color: '#27ae60'
    },
    hard: {
      gravity: 0.4,
      jump: -7.5,
      pipeGap: 165,
      pipeSpeed: 3.5,
      pipeInterval: 80,
      label: 'Hard',
      color: '#e74c3c'
    }
  });
  
  const gameRef = useRef({
    bird: { x: 100, y: 250, velocity: 0, size: 40 },
    pipes: [],
    frameCount: 0,
    wingAngle: 0,
    settings: null
  });

  const getMilestone = (s) => {
    if (s >= 100) return { title: "⭐ LEGENDARY!", subtitle: "You ARE the product" };
    if (s >= 90) return { title: "🏆 IPO Ready!", subtitle: "Ring that bell!" };
    if (s >= 80) return { title: "🌍 Global Scale!", subtitle: "Multi-region deployed" };
    if (s >= 70) return { title: "🔥 Hypergrowth!", subtitle: "10x MoM achieved" };
    if (s >= 60) return { title: "💎 Diamond Hands!", subtitle: "HODL the score" };
    if (s >= 50) return { title: "🦄 Unicorn Mode!", subtitle: "$1B valuation unlocked" };
    if (s >= 40) return { title: "🚀 Series A!", subtitle: "Investors are watching" };
    if (s >= 30) return { title: "🎯 PMF at 80%!", subtitle: "Product-Market Fit incoming" };
    if (s >= 20) return { title: "📈 WAU Attained!", subtitle: "Weekly Active User unlocked" };
    if (s >= 10) return { title: "🛸 UFO Status!", subtitle: "You've achieved UFO status" };
    return null;
  };

  const startGame = useCallback((mode) => {
    const settings = gameModes.current[mode];
    gameRef.current = {
      bird: { x: 100, y: 250, velocity: 0, size: 40 },
      pipes: [],
      frameCount: 0,
      wingAngle: 0,
      settings: settings
    };
    setScore(0);
    setGameState('playing');
  }, []);

  const selectMode = useCallback((mode) => {
    setGameMode(mode);
    gameRef.current.settings = gameModes.current[mode];
    startGame(mode);
  }, [startGame]);

  const backToMenu = () => {
    setGameState('menu');
    setGameMode(null);
  };

  const jump = useCallback(() => {
    if (gameState === 'playing') {
      gameRef.current.bird.velocity = gameRef.current.settings.jump;
    } else if (gameState === 'gameOver') {
      startGame(gameMode);
    }
  }, [gameState, gameMode, startGame]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationId;

    const drawMarioPipe = (x, topHeight, bottomY, canvasHeight, pipeWidth) => {
      const lipHeight = 30;
      const lipOverhang = 8;
      
      // Top pipe
      if (topHeight > 0) {
        const topGradient = ctx.createLinearGradient(x, 0, x + pipeWidth, 0);
        topGradient.addColorStop(0, '#8B0000');
        topGradient.addColorStop(0.3, '#DC143C');
        topGradient.addColorStop(0.7, '#B22222');
        topGradient.addColorStop(1, '#8B0000');
        
        ctx.fillStyle = topGradient;
        ctx.fillRect(x, 0, pipeWidth, topHeight - lipHeight);
        
        const lipGradient = ctx.createLinearGradient(x - lipOverhang, 0, x + pipeWidth + lipOverhang, 0);
        lipGradient.addColorStop(0, '#8B0000');
        lipGradient.addColorStop(0.3, '#FF6347');
        lipGradient.addColorStop(0.7, '#DC143C');
        lipGradient.addColorStop(1, '#8B0000');
        
        ctx.fillStyle = lipGradient;
        ctx.fillRect(x - lipOverhang, topHeight - lipHeight, pipeWidth + lipOverhang * 2, lipHeight);
        
        ctx.strokeStyle = '#FF6347';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(x + 8, 0);
        ctx.lineTo(x + 8, topHeight - lipHeight);
        ctx.stroke();
        
        ctx.strokeStyle = '#4A0000';
        ctx.lineWidth = 2;
        ctx.strokeRect(x - lipOverhang, topHeight - lipHeight, pipeWidth + lipOverhang * 2, lipHeight);
        ctx.strokeRect(x, 0, pipeWidth, topHeight - lipHeight);
      }
      
      // Bottom pipe
      const bottomPipeHeight = canvasHeight - bottomY;
      if (bottomPipeHeight > 0) {
        const lipGradient = ctx.createLinearGradient(x - lipOverhang, 0, x + pipeWidth + lipOverhang, 0);
        lipGradient.addColorStop(0, '#8B0000');
        lipGradient.addColorStop(0.3, '#FF6347');
        lipGradient.addColorStop(0.7, '#DC143C');
        lipGradient.addColorStop(1, '#8B0000');
        
        ctx.fillStyle = lipGradient;
        ctx.fillRect(x - lipOverhang, bottomY, pipeWidth + lipOverhang * 2, lipHeight);
        
        const bottomGradient = ctx.createLinearGradient(x, 0, x + pipeWidth, 0);
        bottomGradient.addColorStop(0, '#8B0000');
        bottomGradient.addColorStop(0.3, '#DC143C');
        bottomGradient.addColorStop(0.7, '#B22222');
        bottomGradient.addColorStop(1, '#8B0000');
        
        ctx.fillStyle = bottomGradient;
        ctx.fillRect(x, bottomY + lipHeight, pipeWidth, bottomPipeHeight - lipHeight);
        
        ctx.strokeStyle = '#FF6347';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(x + 8, bottomY + lipHeight);
        ctx.lineTo(x + 8, canvasHeight);
        ctx.stroke();
        
        ctx.strokeStyle = '#4A0000';
        ctx.lineWidth = 2;
        ctx.strokeRect(x - lipOverhang, bottomY, pipeWidth + lipOverhang * 2, lipHeight);
        ctx.strokeRect(x, bottomY + lipHeight, pipeWidth, bottomPipeHeight - lipHeight);
      }
    };

    const drawBird = (x, y, velocity) => {
      const game = gameRef.current;
      
      game.wingAngle += 0.3;
      const wingFlap = Math.sin(game.wingAngle) * 8;
      
      const rotation = Math.min(Math.max(velocity * 0.05, -0.5), 0.7);
      
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation);
      
      // Body (yellow oval)
      ctx.fillStyle = '#FFD700';
      ctx.beginPath();
      ctx.ellipse(0, 0, 22, 18, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#DAA520';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Belly (lighter yellow)
      ctx.fillStyle = '#FFFACD';
      ctx.beginPath();
      ctx.ellipse(2, 4, 12, 10, 0, 0, Math.PI * 2);
      ctx.fill();
      
      // Red shirt/vest with Noibu text
      ctx.fillStyle = '#E74C3C';
      ctx.beginPath();
      ctx.moveTo(-12, 2);
      ctx.lineTo(12, 2);
      ctx.lineTo(14, 16);
      ctx.lineTo(-14, 16);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#C0392B';
      ctx.lineWidth = 1;
      ctx.stroke();
      
      // Noibu text on shirt
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 7px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('NOIBU', 0, 12);
      
      // Wing
      ctx.fillStyle = '#FFA500';
      ctx.beginPath();
      ctx.ellipse(-8, -2 + wingFlap/2, 10, 6, -0.3 + wingFlap * 0.02, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#FF8C00';
      ctx.lineWidth = 1;
      ctx.stroke();
      
      // Head
      ctx.fillStyle = '#FFD700';
      ctx.beginPath();
      ctx.arc(5, -12, 14, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#DAA520';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Eye white
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.ellipse(10, -14, 7, 8, 0, 0, Math.PI * 2);
      ctx.fill();
      
      // Eye pupil
      ctx.fillStyle = '#000000';
      ctx.beginPath();
      ctx.arc(12, -13, 3, 0, Math.PI * 2);
      ctx.fill();
      
      // Eye shine
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(13, -14, 1.5, 0, Math.PI * 2);
      ctx.fill();
      
      // Beak
      ctx.fillStyle = '#FF6600';
      ctx.beginPath();
      ctx.moveTo(18, -12);
      ctx.lineTo(28, -10);
      ctx.lineTo(18, -6);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#CC5200';
      ctx.lineWidth = 1;
      ctx.stroke();
      
      // Hair/feathers
      ctx.strokeStyle = '#DAA520';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(2, -24);
      ctx.quadraticCurveTo(0, -30, 5, -28);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(6, -25);
      ctx.quadraticCurveTo(8, -32, 12, -28);
      ctx.stroke();
      
      // Feet
      ctx.strokeStyle = '#FF6600';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-5, 16);
      ctx.lineTo(-8, 22);
      ctx.moveTo(-5, 16);
      ctx.lineTo(-3, 22);
      ctx.moveTo(5, 16);
      ctx.lineTo(3, 22);
      ctx.moveTo(5, 16);
      ctx.lineTo(8, 22);
      ctx.stroke();
      
      ctx.restore();
    };

    const gameLoop = () => {
      const game = gameRef.current;
      const pipeWidth = 70;
      const modes = gameModes.current;
      
      // Sky gradient
      const skyGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      skyGradient.addColorStop(0, '#87CEEB');
      skyGradient.addColorStop(1, '#E0F6FF');
      ctx.fillStyle = skyGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Clouds
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      const cloudOffset = (game.frameCount * 0.5) % (canvas.width + 200);
      
      ctx.beginPath();
      ctx.arc(100 - cloudOffset * 0.3, 80, 25, 0, Math.PI * 2);
      ctx.arc(130 - cloudOffset * 0.3, 80, 30, 0, Math.PI * 2);
      ctx.arc(160 - cloudOffset * 0.3, 80, 25, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.beginPath();
      ctx.arc(300 - cloudOffset * 0.2, 150, 20, 0, Math.PI * 2);
      ctx.arc(325 - cloudOffset * 0.2, 150, 25, 0, Math.PI * 2);
      ctx.arc(350 - cloudOffset * 0.2, 150, 20, 0, Math.PI * 2);
      ctx.fill();

      if (gameState === 'playing' && game.settings) {
        const settings = game.settings;
        
        game.bird.velocity += settings.gravity;
        game.bird.y += game.bird.velocity;

        game.frameCount++;
        if (game.frameCount % settings.pipeInterval === 0) {
          const minHeight = 80;
          const maxHeight = canvas.height - settings.pipeGap - 80;
          const height = Math.random() * (maxHeight - minHeight) + minHeight;
          game.pipes.push({
            x: canvas.width,
            topHeight: height,
            bottomY: height + settings.pipeGap,
            scored: false
          });
        }

        game.pipes = game.pipes.filter(pipe => pipe.x + pipeWidth > -20);
        game.pipes.forEach(pipe => {
          pipe.x -= settings.pipeSpeed;

          if (!pipe.scored && pipe.x + pipeWidth < game.bird.x) {
            pipe.scored = true;
            setScore(s => s + 1);
          }

          drawMarioPipe(pipe.x, pipe.topHeight, pipe.bottomY, canvas.height, pipeWidth);

          const birdLeft = game.bird.x - 15;
          const birdRight = game.bird.x + 15;
          const birdTop = game.bird.y - 20;
          const birdBottom = game.bird.y + 18;

          if (birdRight > pipe.x && birdLeft < pipe.x + pipeWidth) {
            if (birdTop < pipe.topHeight || birdBottom > pipe.bottomY) {
              setGameState('gameOver');
              setHighScores(prev => ({
                ...prev,
                [gameMode]: Math.max(prev[gameMode], score)
              }));
            }
          }
        });

        if (game.bird.y + 20 > canvas.height || game.bird.y - 25 < 0) {
          setGameState('gameOver');
          setHighScores(prev => ({
            ...prev,
            [gameMode]: Math.max(prev[gameMode], score)
          }));
        }
      }

      // Draw bird (always visible)
      if (gameState !== 'menu') {
        drawBird(gameRef.current.bird.x, gameRef.current.bird.y, gameRef.current.bird.velocity);
      } else {
        // Animate bird on menu screen
        game.frameCount++;
        const menuBirdY = 250 + Math.sin(game.frameCount * 0.05) * 20;
        drawBird(200, menuBirdY, 0);
      }

      // Draw score during gameplay
      if (gameState === 'playing' && gameMode) {
        ctx.fillStyle = '#FFF';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 4;
        ctx.font = 'bold 40px Arial';
        ctx.strokeText(score, 20, 55);
        ctx.fillText(score, 20, 55);
        
        // Mode indicator
        const modeConfig = modes[gameMode];
        if (modeConfig) {
          ctx.font = 'bold 14px Arial';
          ctx.fillStyle = modeConfig.color;
          ctx.strokeStyle = '#000';
          ctx.lineWidth = 2;
          ctx.textAlign = 'right';
          ctx.strokeText(modeConfig.label + ' Mode', canvas.width - 15, 30);
          ctx.fillText(modeConfig.label + ' Mode', canvas.width - 15, 30);
          ctx.textAlign = 'left';
        }
      }

      animationId = requestAnimationFrame(gameLoop);
    };

    gameLoop();

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [gameState, score, gameMode]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        jump();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [jump]);

  const currentMilestone = getMilestone(score);
  const modes = gameModes.current;

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      gap: '20px',
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh'
    }}>
      <h1 style={{ 
        margin: 0, 
        color: '#FFF',
        textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
        fontSize: '32px'
      }}>
        🐦 Flappy Noibu Bird 🐦
      </h1>
      
      <canvas 
        ref={canvasRef} 
        width={400} 
        height={600}
        onClick={jump}
        style={{ 
          border: '4px solid #333',
          borderRadius: '10px',
          cursor: 'pointer',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
        }}
      />

      <div style={{ textAlign: 'center' }}>
        {gameState !== 'menu' && (
          <div style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            marginBottom: '10px', 
            color: '#FFF',
            textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
          }}>
            Score: {score} | Best: {highScores[gameMode] || 0}
          </div>
        )}
        
        {gameState === 'menu' && (
          <div style={{ 
            color: '#333',
            backgroundColor: 'rgba(255,255,255,0.95)',
            padding: '25px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
            minWidth: '300px'
          }}>
            <p style={{ margin: '0 0 20px 0', fontWeight: 'bold', fontSize: '22px' }}>
              Select Difficulty
            </p>
            
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginBottom: '20px' }}>
              <button
                onClick={() => selectMode('easy')}
                style={{
                  padding: '15px 30px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
                  transition: 'transform 0.1s'
                }}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              >
                🌱 Easy
              </button>
              
              <button
                onClick={() => selectMode('hard')}
                style={{
                  padding: '15px 30px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
                  transition: 'transform 0.1s'
                }}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              >
                🔥 Hard
              </button>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-around',
              fontSize: '12px',
              color: '#666',
              borderTop: '1px solid #eee',
              paddingTop: '15px'
            }}>
              <div>
                <strong style={{ color: '#27ae60' }}>Easy</strong>
                <br />Best: {highScores.easy}
              </div>
              <div>
                <strong style={{ color: '#e74c3c' }}>Hard</strong>
                <br />Best: {highScores.hard}
              </div>
            </div>
            
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
              <p style={{ margin: '0 0 5px 0', fontSize: '12px', fontWeight: 'bold', color: '#667eea' }}>
                🏆 Milestone Achievements:
              </p>
              <p style={{ margin: 0, fontSize: '10px', color: '#888' }}>
                10+ UFO • 20+ WAU • 30+ PMF • 40+ Series A • 50+ Unicorn • 100+ Legend
              </p>
            </div>
          </div>
        )}
        
        {gameState === 'gameOver' && (
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.95)',
            padding: '25px',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
          }}>
            <div style={{ 
              fontSize: '32px', 
              color: '#e74c3c', 
              fontWeight: 'bold',
              marginBottom: '10px'
            }}>
              Game Over! 💥
            </div>
            
            <div style={{ 
              display: 'inline-block',
              padding: '4px 12px',
              backgroundColor: modes[gameMode]?.color || '#666',
              color: 'white',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 'bold',
              marginBottom: '10px'
            }}>
              {modes[gameMode]?.label} Mode
            </div>
            
            <div style={{ fontSize: '20px', color: '#333', marginBottom: '10px' }}>
              Final Score: <strong>{score}</strong>
            </div>
            
            {currentMilestone && (
              <div style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '12px 16px',
                borderRadius: '10px',
                marginBottom: '10px'
              }}>
                <div style={{ fontSize: '18px', color: '#FFD700', fontWeight: 'bold' }}>
                  {currentMilestone.title}
                </div>
                <div style={{ fontSize: '12px', color: '#FFF' }}>
                  {currentMilestone.subtitle}
                </div>
              </div>
            )}
            
            {score > 0 && score === highScores[gameMode] && (
              <div style={{ fontSize: '16px', color: '#27ae60', marginBottom: '10px' }}>
                🎉 New High Score! 🎉
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '15px' }}>
              <button
                onClick={() => startGame(gameMode)}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                🔄 Play Again
              </button>
              <button
                onClick={backToMenu}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                📋 Menu
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={{ 
        backgroundColor: 'rgba(255,255,255,0.95)', 
        padding: '20px', 
        borderRadius: '15px',
        maxWidth: '400px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
      }}>
        <h3 style={{ marginTop: 0, color: '#333', fontSize: '18px' }}>🎮 How to Play:</h3>
        <ul style={{ textAlign: 'left', color: '#555', lineHeight: '1.8', margin: 0, paddingLeft: '20px' }}>
          <li><strong>Click</strong> the canvas or press <strong>SPACE</strong> to flap</li>
          <li>Navigate through the Mario-style pipes</li>
          <li>Each pipe passed = 1 point</li>
          <li>Unlock achievements every 10 points!</li>
        </ul>
      </div>
    </div>
  );
};

export default FlappyBird;